import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Document, Page, pdfjs } from "react-pdf";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  ArrowLeft, ChevronLeft, ChevronRight, ZoomIn, ZoomOut,
  Maximize, Minimize, List, X, Sun, Moon, Coffee, RotateCcw,
} from "lucide-react";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import OutlineTree from "@/components/reader/OutlineTree";
import { useGesture } from "@use-gesture/react";

// PDF.js worker — served from unpkg to avoid webpack worker-loader gymnastics.
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const PREFS_KEY = "aperture_reader_prefs_v1";
const loadPrefs = () => { try { return JSON.parse(localStorage.getItem(PREFS_KEY)) || {}; } catch { return {}; } };
const savePrefs = (p) => { try { localStorage.setItem(PREFS_KEY, JSON.stringify(p)); } catch { /* ignore */ } };

const THEMES = {
  light:  { bg: "#F7F5F0", canvas: "invert(0)",   ink: "#0A2540", muted: "rgba(10,37,64,0.55)" },
  sepia:  { bg: "#E8DCC4", canvas: "sepia(0.18) saturate(1.05)", ink: "#3B2A1A", muted: "rgba(59,42,26,0.55)" },
  dark:   { bg: "#0A2540", canvas: "invert(1) hue-rotate(180deg)", ink: "#F7F5F0", muted: "rgba(247,245,240,0.55)" },
};

export default function Reader() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { user, loading: authLoading } = useAuth();

  const [book, setBook] = useState(null);       // ebook record
  const [error, setError] = useState(null);
  const [pdfDoc, setPdfDoc] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [numPages, setNumPages] = useState(0);
  const [outline, setOutline] = useState([]);

  // Persisted preferences
  const prefs = useMemo(loadPrefs, []);
  const [scale, setScale] = useState(prefs.scale ?? 1.15);
  const [fitMode, setFitMode] = useState(
    prefs.fitMode || (typeof window !== "undefined" && window.innerWidth < 768 ? "page" : "width")
  ); // "width" | "page" | "custom" — mobile defaults to fit-page as requested
  // Default TOC to open on desktop, closed on mobile — respects any prior explicit choice.
  const [sidebarOpen, setSidebarOpen] = useState(
    prefs.sidebarOpen !== undefined
      ? prefs.sidebarOpen
      : (typeof window !== "undefined" && window.innerWidth >= 768)
  );
  const [theme, setTheme] = useState(prefs.theme || "light");
  const [isFullscreen, setIsFullscreen] = useState(false);
  // The scale that would fit the whole page inside the viewport — used to
  // decide whether tap-to-turn is active (navigation mode) or disabled
  // (pan mode, when the user has zoomed past fit-page).
  const [fitPageScale, setFitPageScale] = useState(1);

  const containerRef = useRef(null);
  const canvasWrapRef = useRef(null);

  useEffect(() => savePrefs({ scale, fitMode, sidebarOpen, theme }), [scale, fitMode, sidebarOpen, theme]);
  useEffect(() => {
    const onFs = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFs);
    return () => document.removeEventListener("fullscreenchange", onFs);
  }, []);

  // Disable browser-native pinch-zoom for the lifetime of the reader route.
  // Without this, iOS Safari and Android Chrome zoom the ENTIRE viewport on
  // pinch — including our top/bottom menus and tap zones. We scope it so the
  // rest of the site (catalog, checkout) keeps normal pinch-zoom.
  useEffect(() => {
    const meta = document.querySelector('meta[name="viewport"]');
    const prev = meta?.getAttribute("content");
    meta?.setAttribute(
      "content",
      "width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no"
    );
    // iOS Safari sometimes ignores user-scalable=no. Blocking gesturestart
    // is the belt-and-suspenders fix — it stops the whole-page zoom gesture
    // from ever engaging.
    const stopGesture = (e) => e.preventDefault();
    document.addEventListener("gesturestart", stopGesture, { passive: false });
    document.addEventListener("gesturechange", stopGesture, { passive: false });
    document.addEventListener("gestureend", stopGesture, { passive: false });
    return () => {
      if (prev !== undefined && prev !== null) meta?.setAttribute("content", prev);
      document.removeEventListener("gesturestart", stopGesture);
      document.removeEventListener("gesturechange", stopGesture);
      document.removeEventListener("gestureend", stopGesture);
    };
  }, []);

  // Load the ebook record + saved progress (parallel)
  useEffect(() => {
    if (!user || !slug) return;
    let cancelled = false;
    (async () => {
      try {
        const [bkRes, progRes] = await Promise.all([
          api.get(`/ebooks/${slug}`),
          api.get("/reading-progress").catch(() => ({ data: [] })),
        ]);
        if (cancelled) return;
        setBook(bkRes.data);
        const mine = (progRes.data || []).find(p => p.ebook_id === bkRes.data.id && p.kind === "ebook");
        if (mine?.location?.page) setPageNumber(mine.location.page);
      } catch (e) {
        if (!cancelled) setError("This book isn't available for reading yet.");
      }
    })();
    return () => { cancelled = true; };
  }, [slug, user]);

  // Build the react-pdf `file` prop: use the URL + auth header pattern so PDF.js
  // does its OWN incremental Range fetches (preserving the /api/read HTTP Range
  // streaming). Blob URLs bypass that and force a full download up front.
  // `?v=<pdf_ver>` pins the URL to the current file version — when an admin
  // re-uploads the PDF the record's fingerprint changes, forcing PDF.js and
  // the browser to drop cached bytes and fetch the new file.
  const pdfFile = useMemo(() => {
    if (!book?.id) return null;
    const token = (() => { try { return localStorage.getItem("aperture_token") || ""; } catch { return ""; } })();
    const ver = book.pdf_ver ? `?v=${encodeURIComponent(book.pdf_ver)}` : "";
    return {
      url: `${API}/read/${book.id}${ver}`,
      httpHeaders: token ? { Authorization: `Bearer ${token}` } : {},
      withCredentials: false,
    };
  }, [book?.id, book?.pdf_ver]);

  // Debounced progress save
  const saveTimer = useRef(null);
  const saveProgress = useCallback((page, total) => {
    if (!book?.id || !total) return;
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      api.post(`/reading-progress/${book.id}`, {
        kind: "ebook",
        location: { page, percent: Math.round((page / total) * 1000) / 10 },
        total: { pages: total },
      }).catch(() => {});
    }, 1500);
  }, [book?.id]);
  useEffect(() => { if (numPages) saveProgress(pageNumber, numPages); }, [pageNumber, numPages, saveProgress]);

  // On page turn, if the reader is zoomed past fit-to-page, snap the new page
  // back to the top and horizontally re-center. We can't do this inside a
  // pageNumber useEffect because react-pdf briefly unmounts the page canvas
  // during the swap (scrollWidth collapses to clientWidth for ~40-70ms).
  // Instead we flag a pending reset here and consume it in <Page onRenderSuccess>
  // once the new canvas has laid out at its true scaled width.
  const pendingResetRef = useRef(false);
  // Container-scoped pinch zoom — the OUTER shell (menus, page#, progress bar) stays
  // at 1x while only the inner canvas transform-scales during a pinch gesture. On
  // pinch end we commit the final scale to react-pdf's `scale` prop so PDF.js
  // re-rasterizes at high quality. This is the fix for the mobile "menus zoom with
  // the page" bug — the entire reader has touch-action: pan-x pan-y, which disables
  // browser-native pinch-zoom everywhere.
  const [pinchScale, setPinchScale] = useState(1);
  const pinchStartRef = useRef(1); // scale committed at the start of the current pinch
  const pinchTargetRef = useRef(null);
  useEffect(() => {
    // Always reset scroll to top-center on every page turn (fit-page or zoomed).
    // Previously this only ran when zoomed past fit-page, which caused the "slightly
    // off-centers on tap" symptom at fit-page after any prior scroll.
    pendingResetRef.current = true;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNumber]);

  const handlePageRenderSuccess = useCallback(() => {
    if (!pendingResetRef.current) return;
    pendingResetRef.current = false;
    const el = canvasWrapRef.current;
    if (!el) return;
    el.scrollTop = 0;
    const overflowX = Math.max(0, el.scrollWidth - el.clientWidth);
    el.scrollLeft = overflowX / 2;
  }, []);

  // Fit-to-width auto-scaling driven by container size
  const [autoScale, setAutoScale] = useState(1);
  useEffect(() => {
    const el = canvasWrapRef.current;
    if (!el || !pdfDoc) return;
    const compute = async () => {
      try {
        const page = await pdfDoc.getPage(pageNumber);
        const vp = page.getViewport({ scale: 1 });
        const wrapWidth = el.clientWidth - 32;
        const wrapHeight = el.clientHeight - 32;
        // Always compute what "fit page" would look like — needed to decide
        // between navigation (tap-to-turn) and panning modes on mobile.
        const fitPage = Math.max(0.5, Math.min(3, Math.min(wrapWidth / vp.width, wrapHeight / vp.height)));
        setFitPageScale(fitPage);
        if (fitMode === "width") setAutoScale(Math.max(0.5, Math.min(3, wrapWidth / vp.width)));
        else if (fitMode === "page") setAutoScale(fitPage);
      } catch { /* ignore */ }
    };
    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(el);
    return () => ro.disconnect();
  }, [pdfDoc, pageNumber, fitMode]);
  const effectiveScale = fitMode === "custom" ? scale : autoScale;
  // Track effectiveScale in a ref so the pinch handler closures (registered
  // once on mount inside useGesture) always read the current value.
  const effectiveScaleRef = useRef(effectiveScale);
  useEffect(() => { effectiveScaleRef.current = effectiveScale; }, [effectiveScale]);

  // ─── Custom pinch-zoom handler ──────────────────────────────────────────
  // Applies transform: scale(pinchScale) to the inner canvas only during a
  // pinch. On pinch end we commit the final scale to react-pdf's `scale` prop
  // so PDF.js re-rasterizes at high quality. The outer shell (menus, page#,
  // progress bar, tap zones) is never transformed → they stay sticky.
  useGesture(
    {
      onPinchStart: () => {
        pinchStartRef.current = effectiveScaleRef.current;
      },
      onPinch: ({ offset: [factor] }) => {
        setPinchScale(factor);
      },
      onPinchEnd: ({ offset: [factor] }) => {
        const next = Math.max(0.5, Math.min(3, +(pinchStartRef.current * factor).toFixed(2)));
        setPinchScale(1);
        setFitMode("custom");
        setScale(next);
      },
    },
    {
      target: pinchTargetRef,
      eventOptions: { passive: false },
      pinch: { scaleBounds: { min: 0.4, max: 3.5 }, rubberband: true, from: () => [1, 0] },
    }
  );

  // Keyboard shortcuts
  useEffect(() => {
    const onKey = (e) => {
      if (e.target?.tagName === "INPUT" || e.target?.tagName === "TEXTAREA") return;
      if (e.key === "ArrowRight" || e.key === "PageDown") { e.preventDefault(); setPageNumber(p => Math.min(numPages, p + 1)); }
      else if (e.key === "ArrowLeft" || e.key === "PageUp") { e.preventDefault(); setPageNumber(p => Math.max(1, p - 1)); }
      else if (e.key === "Home") { e.preventDefault(); setPageNumber(1); }
      else if (e.key === "End") { e.preventDefault(); setPageNumber(numPages || 1); }
      else if ((e.key === "+" || e.key === "=") && (e.ctrlKey || e.metaKey)) { e.preventDefault(); setFitMode("custom"); setScale(s => Math.min(3, +(s + 0.1).toFixed(2))); }
      else if (e.key === "-" && (e.ctrlKey || e.metaKey)) { e.preventDefault(); setFitMode("custom"); setScale(s => Math.max(0.5, +(s - 0.1).toFixed(2))); }
      else if (e.key === "f" || e.key === "F") { toggleFullscreen(); }
      else if (e.key === "b" || e.key === "B") { setSidebarOpen(v => !v); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [numPages]);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) containerRef.current?.requestFullscreen?.().catch(() => {});
    else document.exitFullscreen?.().catch(() => {});
  };

  const onDocLoad = async (doc) => {
    setPdfDoc(doc);
    setNumPages(doc.numPages);
    try { const o = await doc.getOutline(); if (o?.length) setOutline(o); } catch { /* ignore */ }
  };

  const jumpToOutlineItem = async (item) => {
    if (!pdfDoc || !item?.dest) return;
    try {
      const dest = typeof item.dest === "string" ? await pdfDoc.getDestination(item.dest) : item.dest;
      if (!dest) return;
      const pageIndex = await pdfDoc.getPageIndex(dest[0]);
      setPageNumber(pageIndex + 1);
    } catch { /* ignore */ }
  };

  if (authLoading) return <div className="min-h-screen flex items-center justify-center text-navy/50">Loading…</div>;
  if (!user) return (
    <div className="min-h-screen flex items-center justify-center px-6 text-center" data-testid="reader-signin-required">
      <div>
        <div className="eyebrow mb-3">Members Only</div>
        <h1 className="aperture-h1 text-3xl mb-6">Sign in to open the reader.</h1>
        <Link to="/login" className="btn-primary px-6 py-3 text-xs uppercase tracking-widest rounded-sm">Sign in</Link>
      </div>
    </div>
  );

  const T = THEMES[theme];
  const percent = numPages ? Math.round((pageNumber / numPages) * 100) : 0;

  return (
    <div ref={containerRef} data-testid="reader-container" style={{ background: T.bg, color: T.ink, height: "100vh", display: "flex", flexDirection: "column", overflow: "hidden" }}>
      {/* Top bar — Layer 2 overlay. flex-shrink-0 keeps it pinned; the flex column below is the only scroller. */}
      <div className="flex-shrink-0 z-30 backdrop-blur-md border-b" style={{ background: T.bg === "#0A2540" ? "rgba(10,37,64,0.85)" : "rgba(247,245,240,0.90)", borderColor: "rgba(0,0,0,0.08)" }}>
        <div className="max-w-full px-3 md:px-6 py-2 md:py-3 flex flex-wrap items-center gap-x-2 gap-y-2">
          <button onClick={() => nav("/library")} data-testid="reader-back-btn" className="inline-flex items-center gap-1 text-xs uppercase tracking-widest flex-shrink-0" style={{ color: T.muted }}>
            <ArrowLeft className="w-4 h-4" /> Library
          </button>
          <div className="hidden md:block truncate flex-1 min-w-0" data-testid="reader-book-title">
            <span className="font-serif text-lg" style={{ color: T.ink }}>{book?.title || "…"}</span>
          </div>
          <div className="flex items-center gap-1 flex-wrap md:flex-nowrap md:ml-auto">
            <button onClick={() => setSidebarOpen(v => !v)} data-testid="reader-toggle-toc" title="Toggle TOC (B)"
              className="p-2 rounded-sm hover:opacity-70" style={{ color: T.ink }}>
              <List className="w-4 h-4" />
            </button>
            <div className="w-px h-4 mx-1" style={{ background: "rgba(0,0,0,0.15)" }} />
            <button onClick={() => { setFitMode("custom"); setScale(s => Math.max(0.5, +(s - 0.1).toFixed(2))); }} data-testid="reader-zoom-out" className="p-2 rounded-sm hover:opacity-70" title="Zoom out"><ZoomOut className="w-4 h-4"/></button>
            <span className="text-xs w-12 md:w-14 text-center tabular-nums" data-testid="reader-scale">{Math.round(effectiveScale * 100)}%</span>
            <button onClick={() => { setFitMode("custom"); setScale(s => Math.min(3, +(s + 0.1).toFixed(2))); }} data-testid="reader-zoom-in" className="p-2 rounded-sm hover:opacity-70" title="Zoom in"><ZoomIn className="w-4 h-4"/></button>
            <div className="w-px h-4 mx-1" style={{ background: "rgba(0,0,0,0.15)" }} />
            <button onClick={() => setFitMode("width")} data-testid="reader-fit-width" className={`px-2 py-1 text-[10px] uppercase tracking-widest rounded-sm ${fitMode === "width" ? "bg-gold text-white" : ""}`} title="Fit width">Width</button>
            <button onClick={() => setFitMode("page")} data-testid="reader-fit-page" className={`px-2 py-1 text-[10px] uppercase tracking-widest rounded-sm ${fitMode === "page" ? "bg-gold text-white" : ""}`} title="Fit page">Page</button>
            <div className="w-px h-4 mx-1" style={{ background: "rgba(0,0,0,0.15)" }} />
            <button onClick={() => setTheme("light")} data-testid="reader-theme-light" className={`p-2 rounded-sm hover:opacity-70 ${theme === "light" ? "text-gold" : ""}`} title="Light"><Sun className="w-4 h-4"/></button>
            <button onClick={() => setTheme("sepia")} data-testid="reader-theme-sepia" className={`p-2 rounded-sm hover:opacity-70 ${theme === "sepia" ? "text-gold" : ""}`} title="Sepia"><Coffee className="w-4 h-4"/></button>
            <button onClick={() => setTheme("dark")} data-testid="reader-theme-dark" className={`p-2 rounded-sm hover:opacity-70 ${theme === "dark" ? "text-gold" : ""}`} title="Dark"><Moon className="w-4 h-4"/></button>
            <div className="w-px h-4 mx-1" style={{ background: "rgba(0,0,0,0.15)" }} />
            <button onClick={toggleFullscreen} data-testid="reader-fullscreen" className="p-2 rounded-sm hover:opacity-70" title="Fullscreen (F)">
              {isFullscreen ? <Minimize className="w-4 h-4"/> : <Maximize className="w-4 h-4"/>}
            </button>
          </div>
        </div>
      </div>

      {/* Body — Layer 1 (the middle zoomable/pannable region). flex-1 min-h-0 confines all scroll to canvas-wrap. */}
      <div className="flex-1 min-h-0 flex relative">
        {/* Mobile backdrop — tap outside TOC to close */}
        {sidebarOpen && (
          <div
            data-testid="reader-toc-backdrop"
            onClick={() => setSidebarOpen(false)}
            className="fixed inset-0 z-40 md:hidden"
            style={{ background: "rgba(0,0,0,0.35)" }}
          />
        )}
        {sidebarOpen && (
          <aside data-testid="reader-toc-sidebar"
            className="fixed top-0 left-0 h-full w-72 z-50 shadow-2xl overflow-y-auto md:static md:h-auto md:w-64 md:z-auto md:shadow-none border-r"
            style={{
              borderColor: "rgba(0,0,0,0.08)",
              background: T.bg === "#0A2540" ? "rgba(10,37,64,0.96)" : (T.bg === "#E8DCC4" ? "rgba(232,220,196,0.96)" : "rgba(247,245,240,0.96)"),
              backdropFilter: "blur(8px)",
              WebkitBackdropFilter: "blur(8px)",
            }}
          >
            <div className="p-4 flex items-center justify-between">
              <div className="eyebrow" style={{ color: T.muted }}>Contents</div>
              <button onClick={() => setSidebarOpen(false)} data-testid="reader-toc-close" className="text-xs" style={{ color: T.muted }} title="Close (B)"><X className="w-3.5 h-3.5"/></button>
            </div>
            <nav className="px-4 pb-6">
              {outline.length === 0 && <div className="text-xs" style={{ color: T.muted }}>No bookmarks in this PDF yet.</div>}
              <OutlineTree items={outline} onSelect={(item) => { jumpToOutlineItem(item); if (window.innerWidth < 768) setSidebarOpen(false); }} theme={T} depth={0} />
            </nav>
          </aside>
        )}

        <div ref={canvasWrapRef} className="flex-1 overflow-auto flex items-start justify-center p-4 relative select-none" data-testid="reader-canvas-wrap"
          style={{ touchAction: "pan-x pan-y" }}
        >
          {/* Navigation mode = scale is at/below fit-page. Above that, tap-zones disable and
              the user can pan the enlarged page with native scroll instead. */}
          {(() => {
            const isNavigable = effectiveScale <= fitPageScale + 0.02;
            return numPages > 0 && !error && isNavigable ? (
              <>
                {/* Invisible tap zones — left/right 20% of the canvas. Sit BELOW the visible
                    edge arrows (arrows keep their z-20) but ABOVE the PDF canvas. */}
                <button
                  data-testid="reader-tap-prev"
                  onClick={() => setPageNumber(p => Math.max(1, p - 1))}
                  aria-label="Previous page (tap zone)"
                  aria-hidden="true"
                  tabIndex={-1}
                  className="absolute left-0 top-0 h-full w-1/5 z-10 bg-transparent focus:outline-none"
                />
                <button
                  data-testid="reader-tap-next"
                  onClick={() => setPageNumber(p => Math.min(numPages, p + 1))}
                  aria-label="Next page (tap zone)"
                  aria-hidden="true"
                  tabIndex={-1}
                  className="absolute right-0 top-0 h-full w-1/5 z-10 bg-transparent focus:outline-none"
                />
              </>
            ) : null;
          })()}
          {/* Edge navigation arrows — ALWAYS visible when there are pages, so the user
              always has an explicit tap target regardless of zoom level. */}
          {numPages > 0 && !error && (
            <>
              <button
                data-testid="reader-edge-prev"
                onClick={() => setPageNumber(p => Math.max(1, p - 1))}
                disabled={pageNumber <= 1}
                aria-label="Previous page"
                className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 z-20 w-10 h-16 md:w-12 md:h-20 rounded-sm flex items-center justify-center disabled:opacity-0 opacity-40 hover:opacity-95 transition-opacity duration-200 backdrop-blur-sm"
                style={{ background: T.bg === "#0A2540" ? "rgba(247,245,240,0.15)" : "rgba(10,37,64,0.12)", color: T.ink }}
              >
                <ChevronLeft className="w-6 h-6" strokeWidth={2} />
              </button>
              <button
                data-testid="reader-edge-next"
                onClick={() => setPageNumber(p => Math.min(numPages, p + 1))}
                disabled={pageNumber >= numPages}
                aria-label="Next page"
                className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 z-20 w-10 h-16 md:w-12 md:h-20 rounded-sm flex items-center justify-center disabled:opacity-0 opacity-40 hover:opacity-95 transition-opacity duration-200 backdrop-blur-sm"
                style={{ background: T.bg === "#0A2540" ? "rgba(247,245,240,0.15)" : "rgba(10,37,64,0.12)", color: T.ink }}
              >
                <ChevronRight className="w-6 h-6" strokeWidth={2} />
              </button>
            </>
          )}
          {error ? (
            <div className="p-12 text-center" style={{ color: T.muted }} data-testid="reader-error">
              <div className="eyebrow mb-2">Reader</div>
              <div className="font-serif text-2xl mb-2" style={{ color: T.ink }}>{error}</div>
              <Link to="/library" className="mt-4 inline-block text-xs uppercase tracking-widest underline" style={{ color: T.ink }}>Back to Library</Link>
            </div>
          ) : (
            <Document
              file={pdfFile || undefined}
              onLoadSuccess={onDocLoad}
              onLoadError={(err) => setError(err?.message?.includes("403") || err?.message?.includes("401") ? "You don't own the eBook format for this title." : "Couldn't load the PDF file.")}
              loading={<div className="p-12" style={{ color: T.muted }}>Loading pages…</div>}
              className="max-w-full"
            >
              {/* Pinch target — this inner element is the ONLY thing that CSS-scales
                  during a live pinch gesture. Outer menus/tap-zones never transform,
                  so they stay physically pinned regardless of zoom. touch-action:none
                  here so the browser hands raw pinch events to useGesture. */}
              <div
                ref={pinchTargetRef}
                data-testid="reader-pinch-target"
                style={{
                  filter: T.canvas,
                  transform: `scale(${pinchScale})`,
                  transformOrigin: "center center",
                  transition: pinchScale === 1 ? "transform 120ms ease-out" : "none",
                  touchAction: "pan-x pan-y",
                  willChange: "transform",
                }}
                className="shadow-2xl"
              >
                <Page pageNumber={pageNumber} scale={effectiveScale} renderTextLayer renderAnnotationLayer onRenderSuccess={handlePageRenderSuccess} />
              </div>
            </Document>
          )}
        </div>
      </div>

      {/* Bottom bar — Layer 2 overlay. flex-shrink-0 keeps it pinned no matter how deep the book pans. */}
      <div className="flex-shrink-0 z-30 border-t backdrop-blur-md" style={{ borderColor: "rgba(0,0,0,0.08)", background: T.bg === "#0A2540" ? "rgba(10,37,64,0.90)" : "rgba(247,245,240,0.92)" }}>
        <div className="px-4 md:px-6 py-3 flex items-center gap-4">
          <button onClick={() => setPageNumber(p => Math.max(1, p - 1))} data-testid="reader-prev-page" disabled={pageNumber <= 1} className="p-2 rounded-sm hover:opacity-70 disabled:opacity-25" style={{ color: T.ink }} title="Previous (←)"><ChevronLeft className="w-4 h-4"/></button>
          <div className="flex items-center gap-2 text-xs" style={{ color: T.muted }}>
            <span>Page</span>
            <input
              data-testid="reader-page-input"
              type="number"
              min={1}
              max={numPages || 1}
              value={pageNumber}
              onChange={(e) => setPageNumber(Math.min(numPages || 1, Math.max(1, parseInt(e.target.value || "1", 10))))}
              className="w-14 px-2 py-1 rounded-sm border text-center tabular-nums text-xs"
              style={{ background: "transparent", borderColor: "rgba(0,0,0,0.15)", color: T.ink }}
            />
            <span>of <span className="tabular-nums" style={{ color: T.ink }} data-testid="reader-total-pages">{numPages || "…"}</span></span>
          </div>
          <button onClick={() => setPageNumber(p => Math.min(numPages || p, p + 1))} data-testid="reader-next-page" disabled={pageNumber >= (numPages || 1)} className="p-2 rounded-sm hover:opacity-70 disabled:opacity-25" style={{ color: T.ink }} title="Next (→)"><ChevronRight className="w-4 h-4"/></button>
          {/* Scrubber */}
          <div className="flex-1 mx-2">
            <input
              data-testid="reader-scrubber"
              type="range"
              min={1}
              max={numPages || 1}
              value={pageNumber}
              onChange={(e) => setPageNumber(parseInt(e.target.value, 10))}
              className="w-full accent-[color:var(--tw-color-gold,#C4A265)]"
            />
          </div>
          <span data-testid="reader-percent" className="text-xs tabular-nums" style={{ color: T.muted }}>{percent}%</span>
          <button onClick={() => setPageNumber(1)} data-testid="reader-restart" className="p-2 rounded-sm hover:opacity-70" style={{ color: T.muted }} title="Restart"><RotateCcw className="w-3.5 h-3.5"/></button>
        </div>
      </div>
    </div>
  );
}
